import React, { FC, memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { TextInputControl } from 'components/_basic/Form/TextInput';
import { BorderedCard } from 'components/BorderedCard';
import { TooltipFormImageUpload } from 'components/BusinessToolForm/components/TooltipFormImageUpload/TooltipFormImageUpload';

import { concatWithDot } from 'helpers/string';

import { IProductAndServicesArrayRowProps } from './types';
import { FieldArrayComponent } from '../../../../../../_basic/Form/FieldArray';
import styles from '../../../../AboutCompany/SWOT/SWOT.module.scss';
import {
  keyFeaturesRowInitialValue,
  PRODUCT_AND_SERVICES_ARRAY_NAMES,
  PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES,
} from '../../utils/form';
import { KeyFeaturesRow } from '../KeyFeaturesRow/KeyFeaturesRow';

export const ProductAndServicesArrayRow: FC<IProductAndServicesArrayRowProps> = memo(
  ({ name, firstLineLabel, secondLineLabel, disabled = false }) => {
    const { control } = useFormContext();
    const { t } = useTranslation();

    const isKeyFeatures = name.split('.')[2] === PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.KEY_FEATURES_AND_BENEFITS;

    return (
      <BorderedCard>
        <Grid container spacing={3} columns={12}>
          <Grid item xs="auto">
            <TooltipFormImageUpload
              width={136}
              height={136}
              name={concatWithDot(name, PRODUCT_AND_SERVICES_ARRAY_NAMES.FILE)}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextInputControl
                  control={control}
                  id={concatWithDot(name, PRODUCT_AND_SERVICES_ARRAY_NAMES.VALUE)}
                  label={firstLineLabel}
                  name={concatWithDot(name, PRODUCT_AND_SERVICES_ARRAY_NAMES.VALUE)}
                  disabled={disabled}
                  maxLength={100}
                />
              </Grid>
              {isKeyFeatures ? (
                <Grid item xs={12}>
                  <FieldArrayComponent
                    name={concatWithDot(name, PRODUCT_AND_SERVICES_ARRAY_NAMES.DESCRIPTION)}
                    fieldArrayStyles={{ rowContainer: styles.rowContainer }}
                    initialItem={keyFeaturesRowInitialValue}
                    buttonText={t('keyFeature')}
                    disabled={disabled}
                    maxLength={5}
                  >
                    {(namePrefix) => <KeyFeaturesRow multiline hiddenLabel name={namePrefix} disabled={disabled} />}
                  </FieldArrayComponent>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <TextInputControl
                    maxLength={500}
                    control={control}
                    id={concatWithDot(name, PRODUCT_AND_SERVICES_ARRAY_NAMES.DESCRIPTION)}
                    label={secondLineLabel}
                    name={concatWithDot(name, PRODUCT_AND_SERVICES_ARRAY_NAMES.DESCRIPTION)}
                    disabled={disabled}
                    multiline
                    hiddenLabel
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </BorderedCard>
    );
  },
);
